import { Button, Icon } from "@/components/common";
import { fmtTelefone } from "@/utils/fmt";
import { Field } from "@/components/onboarding/OnbFields";

import type { OrgContato, OrgForm, OrgSetField } from "./types";

interface Props {
  form: OrgForm;
  setField: OrgSetField;
}

const HEADERS: Record<string, { title: string; sub: string }> = {
  comercial: {
    title: "Contato comercial",
    sub: "Pessoa que cobre comunicação geral, alinhamentos de teses e novas oportunidades.",
  },
  financeiro: {
    title: "Responsável financeiro",
    sub: "Quem recebe faturas, notas de honorário e tem autonomia pra aprovar pagamentos.",
  },
};

// Mapa de identidade -> key estável por contato. Evita key={index}: as
// operações de patch/remove/add preservam a referência dos itens não tocados,
// então a key acompanha o item mesmo após remover um do meio da lista (sem
// deslocar valor/foco para o input errado).
const keyMap = new WeakMap<OrgContato, string>();
let keySeq = 0;
const contatoKey = (c: OrgContato): string => {
  let k = keyMap.get(c);
  if (!k) {
    k = `contato-${++keySeq}`;
    keyMap.set(c, k);
  }
  return k;
};

export function OrgStepContatos({ form, setField }: Props) {
  const contatos = form.contatos;

  const patch = (i: number, p: Partial<OrgContato>) =>
    setField("contatos", contatos.map((c, idx) => (idx === i ? { ...c, ...p } : c)));
  const add = () =>
    setField("contatos", [...contatos, { kind: "outro", name: "", email: "", phone: "" }]);
  const remove = (i: number) =>
    setField("contatos", contatos.filter((_, idx) => idx !== i));

  return (
    <>
      <div className="onb-section-head">
        <div className="onb-step-eyebrow">ETAPA 3 · OPCIONAL</div>
        <h1 className="onb-step-title">Contatos e responsáveis</h1>
        <p className="onb-step-sub">
          Quem o time do Tributo Devido pode procurar quando precisar de uma decisão ou
          alinhamento sobre essa organização.
        </p>
      </div>

      {contatos.map((c, i) => {
        const header = HEADERS[c.kind];
        return (
          <div className="onb-section" key={contatoKey(c)}>
            <div className="onb-contato-head">
              {header ? (
                <div>
                  <div className="onb-section-title">{header.title}</div>
                  <p className="onb-section-sub">{header.sub}</p>
                </div>
              ) : (
                <Field label="Tipo / função">
                  <input
                    className="onb-input"
                    maxLength={16}
                    value={c.kind === "outro" ? "" : c.kind}
                    onChange={(e) => patch(i, { kind: e.target.value || "outro" })}
                    placeholder="Ex: contador, jurídico"
                  />
                </Field>
              )}
              {!c.fixed && (
                <button
                  type="button"
                  className="row-act"
                  onClick={() => remove(i)}
                  aria-label="Remover contato"
                >
                  <Icon name="x" size={14} />
                </button>
              )}
            </div>

            <Field label="Nome">
              <input
                className="onb-input"
                maxLength={100}
                value={c.name}
                onChange={(e) => patch(i, { name: e.target.value })}
                placeholder="João da Silva"
              />
            </Field>
            <div className="onb-field-grid onb-grid-2">
              <Field label="E-mail">
                <input
                  className="onb-input"
                  type="email"
                  maxLength={120}
                  value={c.email}
                  onChange={(e) => patch(i, { email: e.target.value })}
                  placeholder="contato@empresa.com.br"
                />
              </Field>
              <Field label="Telefone">
                <input
                  className="onb-input"
                  inputMode="tel"
                  maxLength={15}
                  value={c.phone}
                  onChange={(e) => patch(i, { phone: fmtTelefone(e.target.value) })}
                  placeholder="(11) 90000-0000"
                />
              </Field>
            </div>
          </div>
        );
      })}

      <Button variant="ghost" iconLeft="plus" onClick={add}>
        Adicionar contato
      </Button>
    </>
  );
}
